import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import styles from './ReplicateCostPage.module.css';

interface Summary {
  balanceUsd: number | null;
  hasBaseline: boolean;
  spend24hUsd: number;
  projMonthUsd: number;
  queueCount: number;
  requests24h: number;
  successRate24h: number | null;
  totalCostAllUsd: number;
  updatedAt: string | null;
}

interface LogRow {
  predictionId: string;
  createdAt: string | null;
  completedAt: string | null;
  status: string;
  model: string;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  predictTimeSec: number | null;
  promptType: string | null;
  userId: number | null;
  userEmail: string | null;
  userName: string | null;
}

interface UserRow {
  userId: number | null;
  email: string | null;
  name: string | null;
  requests: number;
  costUsd: number;
  tokens: number;
}

const REFRESH_MS = 8000;
const PAGE_SIZE = 50;

const fmtUsd = (v: number | null | undefined, digits = 2) =>
  v === null || v === undefined ? '—' : `$${v.toFixed(digits)}`;
const fmtCents = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${(v * 100).toFixed(3)}¢`;
const fmtNum = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('ru-RU');
const fmtDur = (start: string | null, end: string | null, predict: number | null) => {
  if (predict != null) return `${predict.toFixed(1)}с`;
  if (start && end) return `${((new Date(end).getTime() - new Date(start).getTime()) / 1000).toFixed(1)}с`;
  return '—';
};
const fmtTime = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};
const ago = (s: string | null) => {
  if (!s) return '';
  const sec = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 1000));
  if (sec < 60) return `${sec}с назад`;
  return `${Math.round(sec / 60)}м назад`;
};
const who = (r: LogRow | UserRow) => {
  const name = (r as any).userName || (r as any).name;
  const email = (r as any).userEmail || (r as any).email;
  const uid = (r as any).userId;
  if (name) return name;
  if (email) return email;
  if (uid) return `#${uid}`;
  return null;
};

const ReplicateCostPage: React.FC = () => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [queue, setQueue] = useState<LogRow[]>([]);
  const [log, setLog] = useState<LogRow[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [byUser, setByUser] = useState<UserRow[]>([]);
  const [tab, setTab] = useState<'log' | 'users'>('log');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const fetchSummary = useCallback(async () => {
    try {
      const r = await api.get('/admin/replicate/summary');
      setSummary(r.data.summary);
      setErr(null);
    } catch (e: any) {
      setErr(e.response?.data?.error || 'Ошибка загрузки сводки');
    }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      const r = await api.get('/admin/replicate/queue');
      setQueue(r.data.rows || []);
    } catch { /* keep prior */ }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('promptType', typeFilter);
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(page * PAGE_SIZE));
      const r = await api.get(`/admin/replicate/log?${params}`);
      setLog(r.data.rows || []);
      setLogTotal(r.data.total || 0);
    } catch { /* keep prior */ }
  }, [statusFilter, typeFilter, page]);

  const fetchByUser = useCallback(async () => {
    try {
      const r = await api.get('/admin/replicate/by-user?hours=24');
      setByUser(r.data.rows || []);
    } catch { /* keep prior */ }
  }, []);

  const refreshAll = useCallback(() => {
    fetchSummary();
    fetchQueue();
    if (tab === 'log') fetchLog();
    else fetchByUser();
  }, [fetchSummary, fetchQueue, fetchLog, fetchByUser, tab]);

  useEffect(() => {
    refreshAll();
    timer.current = window.setInterval(refreshAll, REFRESH_MS);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [refreshAll]);

  const balanceClass = summary?.balanceUsd == null ? '' :
    summary.balanceUsd <= 10 ? styles.danger : summary.balanceUsd <= 30 ? styles.warn : styles.ok;
  const totalPages = Math.max(1, Math.ceil(logTotal / PAGE_SIZE));

  const promptTypes = Array.from(new Set(log.map(r => r.promptType).filter(Boolean))) as string[];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.title}>Replicate — расход и запросы в реальном времени</div>
        <div className={styles.live}><span className={styles.dot} /> авто-обновление {REFRESH_MS / 1000}с
          {summary?.updatedAt && <span className={styles.muted}>· ledger {ago(summary.updatedAt)}</span>}
        </div>
      </div>

      {err && <div className={styles.empty} style={{ color: '#dc2626' }}>{err}</div>}

      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Баланс</div>
          <div className={`${styles.cardValue} ${balanceClass}`}>
            {summary?.hasBaseline ? fmtUsd(summary?.balanceUsd) : '—'}
          </div>
          {!summary?.hasBaseline && <div className={styles.cardSub}>задайте баланс (top-up)</div>}
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Расход 24ч</div>
          <div className={styles.cardValue}>{fmtUsd(summary?.spend24hUsd, 3)}</div>
          <div className={styles.cardSub}>≈ в день</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Прогноз/мес</div>
          <div className={styles.cardValue}>{fmtUsd(summary?.projMonthUsd)}</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>В очереди</div>
          <div className={`${styles.cardValue} ${(summary?.queueCount || 0) > 20 ? styles.warn : ''}`}>
            {fmtNum(summary?.queueCount)}
          </div>
          <div className={styles.cardSub}>выполняется сейчас</div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Успешность 24ч</div>
          <div className={`${styles.cardValue} ${summary?.successRate24h != null && summary.successRate24h < 95 ? styles.warn : styles.ok}`}>
            {summary?.successRate24h != null ? `${summary.successRate24h.toFixed(1)}%` : '—'}
          </div>
        </div>
        <div className={styles.card}>
          <div className={styles.cardLabel}>Запросов 24ч</div>
          <div className={styles.cardValue}>{fmtNum(summary?.requests24h)}</div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>🔴 Живая очередь ({queue.length})</div>
        {queue.length === 0 ? (
          <div className={styles.empty}>Сейчас нет выполняющихся запросов</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr>
                <th>Статус</th><th>Модель</th><th>Тип</th><th>Пользователь</th><th>Создан</th><th className={styles.mono}>ID</th>
              </tr></thead>
              <tbody>
                {queue.map(r => (
                  <tr key={r.predictionId}>
                    <td><span className={`${styles.badge} ${styles[r.status] || ''}`}>{r.status}</span></td>
                    <td>{r.model || '—'}</td>
                    <td>{r.promptType || <span className={styles.muted}>фон/система</span>}</td>
                    <td>{who(r) || <span className={styles.muted}>—</span>}</td>
                    <td>{ago(r.createdAt)}</td>
                    <td className={styles.mono}>{r.predictionId.slice(0, 10)}…</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.tabs}>
          <button className={`${styles.tab} ${tab === 'log' ? styles.active : ''}`} onClick={() => setTab('log')}>Запросы</button>
          <button className={`${styles.tab} ${tab === 'users' ? styles.active : ''}`} onClick={() => setTab('users')}>По пользователям</button>
        </div>

        {tab === 'log' && (
          <>
            <div className={styles.filters}>
              <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }}>
                <option value="all">Все статусы</option>
                <option value="succeeded">succeeded</option>
                <option value="failed">failed</option>
                <option value="canceled">canceled</option>
                <option value="processing">processing</option>
              </select>
              <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
                <option value="all">Все типы</option>
                {promptTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr>
                  <th>Время</th><th>Пользователь</th><th>Тип (тема)</th><th>Статус</th><th>Модель</th>
                  <th>Input</th><th>Output</th><th>Стоимость</th><th>Длит.</th>
                </tr></thead>
                <tbody>
                  {log.length === 0 ? (
                    <tr><td colSpan={9} className={styles.empty}>Нет данных</td></tr>
                  ) : log.map(r => (
                    <tr key={r.predictionId}>
                      <td>{fmtTime(r.createdAt)}</td>
                      <td>{who(r) || <span className={styles.muted}>фон/система</span>}</td>
                      <td>{r.promptType || <span className={styles.muted}>enrichment/фон</span>}</td>
                      <td><span className={`${styles.badge} ${styles[r.status] || ''}`}>{r.status}</span></td>
                      <td>{r.model || '—'}</td>
                      <td>{fmtNum(r.inputTokens)}</td>
                      <td>{fmtNum(r.outputTokens)}</td>
                      <td>{fmtCents(r.costUsd)}</td>
                      <td>{fmtDur(r.createdAt, r.completedAt, r.predictTimeSec)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.pager}>
              <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>← Назад</button>
              <span>Стр. {page + 1} из {totalPages} · {fmtNum(logTotal)} запросов</span>
              <button disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Вперёд →</button>
            </div>
          </>
        )}

        {tab === 'users' && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr>
                <th>Пользователь</th><th>Email</th><th>Запросы 24ч</th><th>Токены</th><th>Стоимость 24ч</th>
              </tr></thead>
              <tbody>
                {byUser.length === 0 ? (
                  <tr><td colSpan={5} className={styles.empty}>Нет данных</td></tr>
                ) : byUser.map((r, i) => (
                  <tr key={r.userId ?? `sys-${i}`}>
                    <td>{r.userId ? (who(r) || `#${r.userId}`) : <span className={styles.muted}>Фон (enrichment/система)</span>}</td>
                    <td>{r.email || <span className={styles.muted}>—</span>}</td>
                    <td>{fmtNum(r.requests)}</td>
                    <td>{fmtNum(r.tokens)}</td>
                    <td>{fmtUsd(r.costUsd, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplicateCostPage;
